import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import SectionHeader from "../components/ui/SectionHeader";
import { useCreateTaskMutation, useDeleteTaskMutation, useGetEmployeesQuery, useGetTasksQuery, useUpdateTaskMutation } from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { shortDate } from "../utils/formatters";

const columns = [
  { id: "todo", title: "To do" },
  { id: "in_progress", title: "In progress" },
  { id: "done", title: "Done" },
];

const priorityOptions = ["low", "medium", "high", "urgent"];

const emptyTaskForm = {
  title: "",
  description: "",
  status: "todo",
  priority: "medium",
  assignee_id: "",
  due_date: "",
};

const normalizeTaskPayload = (formValues) => ({
  title: formValues.title.trim(),
  description: formValues.description.trim(),
  status: formValues.status,
  priority: formValues.priority,
  assignee_id: formValues.assignee_id ? Number(formValues.assignee_id) : null,
  due_date: formValues.due_date || null,
});

const TasksPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetTasksQuery();
  const { data: employees } = useGetEmployeesQuery();
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [tasks, setTasks] = useState([]);
  const [createForm, setCreateForm] = useState(emptyTaskForm);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setTasks(data?.data || []);
  }, [data]);

  const employeesById = useMemo(() => {
    const mappedEmployees = new Map();
    (employees?.data || []).forEach((employee) => {
      mappedEmployees.set(employee.id, `${employee.first_name} ${employee.last_name}`);
    });
    return mappedEmployees;
  }, [employees]);

  const handleCreateChange = (field, value) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateTask = async (event) => {
    event.preventDefault();
    const payload = normalizeTaskPayload(createForm);

    if (!payload.title) {
      setSubmitError("Task title is required.");
      return;
    }

    try {
      const createdTask = await createTask({
        ...payload,
        sort_order: tasks.length + 1,
      }).unwrap();
      const createdId = createdTask?.data?.id ?? createdTask?.id;

      setCreateForm(emptyTaskForm);
      setSubmitError("");
      if (createdId) {
        navigate(`/tasks/${createdId}/edit`);
      }
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to create task."));
    }
  };

  const handleDrop = async (status, task) => {
    try {
      await updateTask({ id: task.id, status }).unwrap();
      setSubmitError("");
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to update task."));
    }
  };

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Daily Task System"
        title="Create detailed tasks, edit them cleanly, and move them across the board."
      />

      {submitError ? <small className="danger-text">{submitError}</small> : null}

      <div className="tasks-workspace">
        <form className="panel form-panel task-form-panel" onSubmit={handleCreateTask}>
          <div className="task-form-panel__header">
            <div>
              <small className="eyebrow">Create task</small>
              <h3>Add a detailed todo</h3>
            </div>
            <button className="primary-button" type="submit" disabled={isCreating}>
              {isCreating ? "Saving..." : "Create task"}
            </button>
          </div>

          <input
            value={createForm.title}
            onChange={(event) => handleCreateChange("title", event.target.value)}
            placeholder="Detailed task title"
          />
          <textarea
            rows="4"
            value={createForm.description}
            onChange={(event) => handleCreateChange("description", event.target.value)}
            placeholder="Description, next step, or notes"
          />
          <div className="form-row task-form-grid">
            <select value={createForm.status} onChange={(event) => handleCreateChange("status", event.target.value)}>
              {columns.map((column) => (
                <option key={column.id} value={column.id}>{column.title}</option>
              ))}
            </select>
            <select value={createForm.priority} onChange={(event) => handleCreateChange("priority", event.target.value)}>
              {priorityOptions.map((priority) => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
            <select value={createForm.assignee_id} onChange={(event) => handleCreateChange("assignee_id", event.target.value)}>
              <option value="">Unassigned</option>
              {(employees?.data || []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.first_name} {employee.last_name}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={createForm.due_date}
              onChange={(event) => handleCreateChange("due_date", event.target.value)}
            />
          </div>
        </form>

        <section className="panel task-form-panel task-form-panel--placeholder">
          <div className="task-form-panel__header">
            <div>
              <small className="eyebrow">Update task</small>
              <h3>Edit section is hidden at first</h3>
              <p className="task-form-panel__hint">Click any task card below and it will open on a separate page for editing.</p>
            </div>
          </div>
        </section>
      </div>

      <div className="kanban-board">
        {columns.map((column) => (
          <section
            key={column.id}
            className="kanban-column"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              const taskId = Number(event.dataTransfer.getData("taskId"));
              const task = tasks.find((item) => item.id === taskId);
              if (task) {
                handleDrop(column.id, task);
              }
            }}
          >
            <header>
              <h3>{column.title}</h3>
              <span>{tasks.filter((task) => task.status === column.id).length}</span>
            </header>

            {isLoading ? <div className="loading-state">Loading tasks...</div> : null}

            {!isLoading && !tasks.filter((task) => task.status === column.id).length ? (
              <div className="empty-state">No tasks here yet.</div>
            ) : null}

            {tasks
              .filter((task) => task.status === column.id)
              .map((task) => (
                <article
                  key={task.id}
                  className="task-card"
                  draggable
                  onClick={() => navigate(`/tasks/${task.id}/edit`)}
                  onDragStart={(event) => event.dataTransfer.setData("taskId", String(task.id))}
                >
                  <div className="task-card__top">
                    <strong>{task.title}</strong>
                    <span className={`status-badge ${task.priority === "urgent" || task.priority === "high" ? "status-badge--danger" : task.priority === "medium" ? "status-badge--warning" : "status-badge--success"}`}>
                      {task.priority}
                    </span>
                  </div>
                  <p>{task.description || "No description yet."}</p>
                  <footer className="task-card__footer">
                    <span>{shortDate(task.due_date) || "No due date"}</span>
                    <span>{employeesById.get(task.assignee_id) || "Unassigned"}</span>
                    <button
                      className="table-action table-action--danger"
                      type="button"
                      onClick={async (event) => {
                        event.stopPropagation();
                        try {
                          await deleteTask(task.id).unwrap();
                          setSubmitError("");
                        } catch (error) {
                          setSubmitError(getApiErrorMessage(error, "Unable to delete task."));
                        }
                      }}
                    >
                      Delete
                    </button>
                  </footer>
                </article>
              ))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default TasksPage;
